/**
 * Утилиты для работы с иерархией типов объектов
 * 
 * Иерархия строится на основе рубрики (kls_rubrika) - ltree пути вида "1.2.3"
 */

import type { ObjectType } from "@/types/api-v2";

export interface ObjectTypeNode extends ObjectType {
  children: ObjectTypeNode[];
  level: number;
}

/**
 * Строит иерархическое дерево из плоского списка типов объектов на основе рубрики
 */
export function buildObjectTypesHierarchy(objectTypes: ObjectType[]): ObjectTypeNode[] {
  if (!objectTypes || objectTypes.length === 0) {
    console.log('[buildObjectTypesHierarchy] No objectTypes provided');
    return [];
  }

  console.log('[buildObjectTypesHierarchy] Processing', objectTypes.length, 'items');

  // Создаем словарь для быстрого доступа по рубрике и ID
  const nodesByRubrika = new Map<string, ObjectTypeNode>();
  const nodesById = new Map<number, ObjectTypeNode>();
  
  // Сначала создаем все узлы
  objectTypes.forEach((type) => {
    const rubrika = type.rubrika || '';
    const node: ObjectTypeNode = {
      ...type,
      children: [],
      level: 0,
    };
    
    if (rubrika) {
      nodesByRubrika.set(rubrika, node);
    }
    nodesById.set(type.id, node);
  });
  
  console.log('[buildObjectTypesHierarchy] nodesByRubrika size:', nodesByRubrika.size);
  
  // Строим дерево на основе рубрики
  const rootNodes: ObjectTypeNode[] = [];
  let childCount = 0;
  
  objectTypes.forEach((type) => {
    const node = nodesById.get(type.id)!;
    const rubrika = type.rubrika || '';
    
    if (!rubrika) {
      // Нет рубрики - корневой узел
      rootNodes.push(node);
      return;
    }
    
    // Определяем уровень вложенности по количеству точек в рубрике
    const parts = rubrika.split('.').filter(p => p.length > 0);
    
    if (parts.length === 0) {
      // Пустая рубрика - корневой узел
      rootNodes.push(node);
      return;
    }
    
    node.level = parts.length - 1;
    
    if (parts.length > 1) {
      // Есть родитель - находим его по родительской рубрике
      const parentRubrika = parts.slice(0, -1).join('.');
      const parent = nodesByRubrika.get(parentRubrika);
      
      if (parent) {
        parent.children.push(node);
        childCount++;
      } else {
        // Родитель не найден - добавляем в корень
        console.warn(`[buildObjectTypesHierarchy] Parent not found for rubrika: ${rubrika}, expected parent: ${parentRubrika}`);
        rootNodes.push(node);
      }
    } else {
      // Корневой узел (рубрика без точек или один сегмент)
      rootNodes.push(node);
    }
  });
  
  console.log('[buildObjectTypesHierarchy] Result: rootNodes:', rootNodes.length, 'children attached:', childCount);
  
  // Сортируем узлы по рубрике (это сохранит правильный порядок иерархии)
  const sortNodes = (nodes: ObjectTypeNode[]) => {
    nodes.sort((a, b) => {
      const rubrikaA = a.rubrika || '';
      const rubrikaB = b.rubrika || '';
      
      if (!rubrikaA && !rubrikaB) return 0;
      if (!rubrikaA) return 1;
      if (!rubrikaB) return -1;
      
      // Сравниваем по числовым значениям в рубрике
      return rubrikaA.localeCompare(rubrikaB, undefined, { numeric: true, sensitivity: 'base' });
    });
    
    nodes.forEach((node) => {
      if (node.children.length > 0) {
        sortNodes(node.children);
      }
    });
  };
  
  sortNodes(rootNodes);
  
  return rootNodes;
}

/**
 * Преобразует иерархическое дерево в плоский список для использования в select
 * Сохраняет уровень вложенности для визуального отображения через CSS
 * 
 * displayName использует name_short (краткое название) если доступно,
 * иначе полное name. При наведении показывается полное название (title).
 */
export function flattenObjectTypesHierarchy(
  nodes: ObjectTypeNode[],
  level: number = 0
): Array<ObjectTypeNode & { displayName: string; indentLevel: number }> {
  const result: Array<ObjectTypeNode & { displayName: string; indentLevel: number }> = [];
  
  nodes.forEach((node) => {
    result.push({
      ...node,
      // Используем краткое название если есть, иначе полное
      displayName: node.name_short || node.name,
      indentLevel: level,
    });
    
    if (node.children.length > 0) {
      result.push(...flattenObjectTypesHierarchy(node.children, level + 1));
    }
  });
  
  console.log('[flattenObjectTypesHierarchy] Result:', result.length, 'items');
  
  return result;
}
